import React from "react"
import Tabs from "@material-ui/core/Tabs"
import Tab from "@material-ui/core/Tab"
import OrgGeneralSettings from "../../../../organisations/components/OrgGeneralSettings"
import OrgNotificationSettings from "../../../../organisations/components/OrgNotificationSettings"
import OrgProjectSettingsLayout from "./projects"
import { makeStyles, Theme } from "@material-ui/core/styles"
import { useAppContext } from "../../../../app-context/use-app-context"
import { useTranslation } from "react-i18next"
import { Grid, Box, Typography, CircularProgress } from "@material-ui/core"
import { useAppDispatch } from "../../../../store"
import { FetchOrganisation } from "../../../../organisations/store/actions"
import { FetchOrgMemberships } from "../../../../memberships/store"
import { unwrapResult } from "@reduxjs/toolkit"
import { useAsync } from "../../../../hooks/use-async"
import { Link, Route, Switch, useRouteMatch } from "react-router-dom"
import { paths } from "../../../../paths"
import { useUrlWithContext } from "../../../../hooks/use-url-with-context"
import OrgHolidaysList from "./holidays"
import OrgTaskSettings from "./tasks"
import OrgHourReportSettings from "../../../../organisations/components/hour-report-settings"
import { useGetHourReportSettingsQuery } from "../../../../organisations/api"
import {
  canUpdateHourReportSettings,
  HourReportSettingsViewModel,
} from "../../../../organisations/api/hour-report-settings"
import { Alert } from "@material-ui/lab"

const useLoader = (orgId: string) => {
  const dispatch = useAppDispatch()
  const loader = React.useCallback(async () => {
    dispatch({ type: `load_org_home_route` })
    const results = await Promise.all([dispatch(FetchOrganisation(orgId)), dispatch(FetchOrgMemberships(orgId))])
    results.forEach(unwrapResult)
  }, [dispatch, orgId])
  return useAsync<void, any>(loader)
}

const OrgSettingsLayout = () => {
  const classes = useStyles()
  const { appContext } = useAppContext()
  const { mainContext } = appContext
  const translations = useTranslations()
  const orgId = mainContext?.id as string
  const { createPathWithContext } = useUrlWithContext()
  const { isSuccess, isPending } = useLoader(orgId)
  const isNotifications = useRouteMatch(paths.orgNotificationSettings())
  const isProjects = useRouteMatch(paths.orgProjectSettings())
  const isTasks = useRouteMatch(paths.orgTaskSettings())
  const isHolidays = useRouteMatch(paths.orgHolidaySettings())
  const isHourReports = useRouteMatch(paths.orgHourReportSettings())
  const initialTab = isHourReports ? 5 : isHolidays ? 4 : isTasks ? 3 : isProjects ? 2 : isNotifications ? 1 : 0
  const [currentTab, setCurrentTab] = React.useState(initialTab)
  const { data: hourReportSettingsData, isLoading } = useGetHourReportSettingsQuery(orgId)

  if (isPending() || isLoading) return <CircularProgress />
  if (!mainContext || mainContext.type !== "org" || !isSuccess()) return null

  const generalSettingsPath = createPathWithContext(paths.orgGeneralSettings())
  const notificationSettingsPath = createPathWithContext(paths.orgNotificationSettings())
  const projectSettingsPath = createPathWithContext(paths.orgProjectSettings())
  const holidaySettingsPath = createPathWithContext(paths.orgHolidaySettings())
  const taskSettingsPath = createPathWithContext(paths.orgTaskSettings())
  const hourReportSettingsPath = createPathWithContext(paths.orgHourReportSettings())
  const canUpdateSettings = canUpdateHourReportSettings(hourReportSettingsData)

  return (
    <div className={classes.container}>
      <Grid item style={{ marginBottom: 32 }}>
        <Typography variant="h4" data-test="page-header">
          <Box color="text.secondary" component="span">
            {mainContext.name}
          </Box>
          {" - "}
          {translations.settings}
        </Typography>
      </Grid>
      <Tabs
        variant="scrollable"
        value={currentTab}
        onChange={(e, v) => setCurrentTab(v)}
        indicatorColor="primary"
        className={classes.tabs}
      >
        <Tab
          component={Link}
          to={generalSettingsPath}
          className={classes.tab}
          label={translations.general}
          disableRipple
        />
        <Tab
          component={Link}
          to={notificationSettingsPath}
          className={classes.tab}
          label={translations.notifications}
          disableRipple
        />
        <Tab
          component={Link}
          to={projectSettingsPath}
          className={classes.tab}
          label={translations.projectsLabel}
          disableRipple
        />
        <Tab
          component={Link}
          to={taskSettingsPath}
          className={classes.tab}
          label={translations.tasksLabel}
          disableRipple
        />
        <Tab
          component={Link}
          to={holidaySettingsPath}
          className={classes.tab}
          disableRipple
          label={translations.holidaysLabel}
        />
        {canUpdateSettings && (
          <Tab
            component={Link}
            to={hourReportSettingsPath}
            className={classes.tab}
            disableRipple
            label={translations.hourReportsLabel}
          />
        )}
      </Tabs>
      <Switch>
        <Route
          path={paths.orgHourReportSettings()}
          render={() => renderOrgHourReportSettings(hourReportSettingsData)}
        />
        <Route path={paths.orgHolidaySettings()} component={OrgHolidaysList} />
        <Route path={paths.orgProjectSettings()} component={OrgProjectSettingsLayout} />
        <Route path={paths.orgTaskSettings()} component={OrgTaskSettings} />
        <Route path={paths.orgNotificationSettings()} component={OrgNotificationSettings} />
        <Route path={paths.orgGeneralSettings()} component={OrgGeneralSettings} />
      </Switch>
    </div>
  )
}

function renderOrgHourReportSettings(settings?: HourReportSettingsViewModel) {
  if (!settings) {
    return <Alert severity="error">Failed to load hour report settings</Alert>
  }
  return (
    <OrgHourReportSettings
      workflow={settings.hourReportSettings.workflow}
      canUpdateWorkflow={settings.canUpdateWorkflow}
      canUpdateAdminSelfReview={settings.canUpdateAdminSelfReview}
    />
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: 950,
    margin: "auto",
    paddingTop: 8,
    paddingBottom: 32,
    borderRadius: 8,
  },
  tabs: { borderBottom: `1px solid ${theme.palette.divider}` },
  tab: { minWidth: 120 },
  tabPanel: { padding: theme.spacing(3, 0) },
}))

const defaultTranslations = {
  settings: "Settings",
  general: "General",
  notifications: "Notifications",
  projectsLabel: "Projects",
  holidaysLabel: "Holidays",
  tasksLabel: "Tasks",
  hourReportsLabel: "Hour reports",
}

const useTranslations = (): Translations => {
  const { t } = useTranslation()
  const { t: tOrg } = useTranslation("org")

  return {
    settings: t("settings", { defaultValue: defaultTranslations.settings }),
    general: t("general", { defaultValue: defaultTranslations.general }),
    notifications: t("notifications", { defaultValue: defaultTranslations.notifications }),
    projectsLabel: t("appNavBar.projectsLabel", { defaultValue: defaultTranslations.projectsLabel }),
    holidaysLabel: t("holidaysLabel", { defaultValue: defaultTranslations.holidaysLabel }),
    tasksLabel: t("appNavBar.tasksLabel", { defaultValue: defaultTranslations.tasksLabel }),
    hourReportsLabel: tOrg("orgSettingsPage.hourReportsLabel", {
      defaultValue: defaultTranslations.hourReportsLabel,
    }),
  }
}

type Translations = typeof defaultTranslations

export default OrgSettingsLayout
